import {
    ADDRESS_CLAMP_TO_EDGE,
    FILTER_LINEAR,
    GraphicsDevice,
    PIXELFORMAT_RGBA8,
    Texture
} from 'playcanvas';

type VideoTexturePlaybackMode = 'realtime' | 'per-frame';

class VideoTextureSource {
    readonly texture: Texture;
    readonly video: HTMLVideoElement;
    readonly ready: Promise<void>;
    readonly usesCanvasSource: boolean;

    private readonly blobUrl: string;
    private readonly canvas: HTMLCanvasElement | null;
    private readonly canvasContext: CanvasRenderingContext2D | null;
    private readySettled = false;
    private readyResolve: (() => void) | null = null;
    private readyReject: ((reason?: unknown) => void) | null = null;
    private removeReadyListeners: (() => void) | null = null;
    private destroyed = false;
    private _playbackMode: VideoTexturePlaybackMode = 'per-frame';
    private realtimePlaybackDesired = false;
    private syncSequence = 0;

    constructor(file: File, graphicsDevice: GraphicsDevice, options?: { preserveAlpha?: boolean }) {
        this.blobUrl = URL.createObjectURL(file);
        this.usesCanvasSource = !!options?.preserveAlpha;

        this.texture = new Texture(graphicsDevice, {
            name: `videoTexture:${file.name}`,
            width: 2,
            height: 2,
            format: PIXELFORMAT_RGBA8,
            mipmaps: false,
            minFilter: FILTER_LINEAR,
            magFilter: FILTER_LINEAR,
            addressU: ADDRESS_CLAMP_TO_EDGE,
            addressV: ADDRESS_CLAMP_TO_EDGE
        });

        if (this.usesCanvasSource) {
            this.canvas = document.createElement('canvas');
            this.canvasContext = this.canvas.getContext('2d', { alpha: true });
        } else {
            this.canvas = null;
            this.canvasContext = null;
        }

        this.video = document.createElement('video');
        this.video.muted = true;
        this.video.defaultMuted = true;
        this.video.loop = true;
        this.video.autoplay = false;
        this.video.playsInline = true;
        this.video.preload = 'auto';
        this.video.crossOrigin = 'anonymous';
        this.video.setAttribute('muted', '');
        this.video.setAttribute('playsinline', '');
        this.video.setAttribute('webkit-playsinline', '');
        this.video.src = this.blobUrl;

        this.ready = new Promise<void>((resolve, reject) => {
            this.readyResolve = resolve;
            this.readyReject = reject;

            const onLoadedData = () => {
                if (this.destroyed) {
                    this.rejectReady(new Error('Video texture source destroyed before bind completed'));
                    return;
                }

                try {
                    this.syncTextureSource();
                    this.texture.upload();
                    if (this._playbackMode === 'realtime' && this.realtimePlaybackDesired) {
                        void this.play();
                    } else {
                        this.forcePause();
                    }
                    this.resolveReady();
                } catch (error) {
                    this.rejectReady(error);
                }
            };

            const onError = () => {
                const mediaError = this.video.error;
                const error = new Error(mediaError?.message || `Failed to load video texture: ${file.name}`);
                this.rejectReady(error);
            };

            this.video.addEventListener('loadeddata', onLoadedData, { once: true });
            this.video.addEventListener('error', onError, { once: true });

            this.removeReadyListeners = () => {
                this.video.removeEventListener('loadeddata', onLoadedData);
                this.video.removeEventListener('error', onError);
                this.removeReadyListeners = null;
            };

            this.video.load();
        });
    }

    async play() {
        this.realtimePlaybackDesired = true;
        if (this.destroyed) return;
        if (this._playbackMode !== 'realtime') return;

        try {
            this.video.autoplay = true;
            await this.video.play();
        } catch {
            // Autoplay can still be blocked in some environments; keep the texture bound regardless.
        }
    }

    pause() {
        this.realtimePlaybackDesired = false;
        this.forcePause();
    }

    setPlaybackMode(mode: VideoTexturePlaybackMode) {
        if (this.destroyed) return;

        const nextMode: VideoTexturePlaybackMode = mode === 'per-frame' ? 'per-frame' : 'realtime';
        if (nextMode === this._playbackMode) return;

        this._playbackMode = nextMode;
        if (nextMode === 'per-frame') {
            this.forcePause();
        } else if (this.realtimePlaybackDesired) {
            void this.play();
        }
    }

    get playbackMode() {
        return this._playbackMode;
    }

    async syncToTimelineTime(frameTime: number, timelineFrameRate: number, precise = false) {
        if (this.destroyed) return;

        const frameRate = Number.isFinite(timelineFrameRate) && timelineFrameRate > 0 ? timelineFrameRate : 30;
        const targetTime = this.normalizeTargetTime(frameTime / frameRate);
        const syncId = ++this.syncSequence;

        this.setPlaybackMode('per-frame');
        await this.waitForReadyFrame();
        if (this.destroyed || syncId !== this.syncSequence) return false;

        await this.seekToTime(targetTime, syncId, frameRate, precise);
        if (this.destroyed || syncId !== this.syncSequence) return false;

        this.syncTextureSource();
        this.texture.upload();
        return true;
    }

    setLoop(loop: boolean) {
        if (this.destroyed) return;
        this.video.loop = !!loop;
    }

    get loop() {
        return this.video.loop;
    }

    setPlaybackRate(rate: number) {
        if (this.destroyed) return;
        const next = Number.isFinite(rate) && rate > 0 ? rate : 1;
        this.video.playbackRate = next;
        this.video.defaultPlaybackRate = next;
    }

    get playbackRate() {
        return this.video.playbackRate;
    }

    get isPlaying() {
        return this._playbackMode === 'realtime' && !this.video.paused && !this.video.ended;
    }

    update() {
        if (this.destroyed) return;

        if (this._playbackMode !== 'realtime') {
            // Defensive: if the video is somehow still playing in per-frame
            // mode (browser autoplay quirk), force-pause it.
            if (!this.video.paused && !this.video.ended) {
                this.forcePause();
            }
            return;
        }

        if (this.video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) return;
        if (this.video.paused || this.video.ended) return;

        if (this.usesCanvasSource) {
            this.syncTextureSource();
        }

        this.texture.upload();
    }

    destroy() {
        if (this.destroyed) return;

        this.destroyed = true;
        this.removeReadyListeners?.();
        this.rejectReady(new Error('Video texture source destroyed'));

        try {
            this.video.pause();
            this.video.src = '';
            this.video.removeAttribute('src');
            this.video.load();
        } catch {
            // ignore
        }

        URL.revokeObjectURL(this.blobUrl);
        this.texture.destroy();
    }

    private forcePause() {
        if (this.destroyed) return;

        try {
            // Disable autoplay to prevent the browser from restarting
            // muted video playback after pause.
            this.video.autoplay = false;
            this.video.pause();
        } catch {
            // ignore
        }
    }

    private syncTextureSource() {
        if (!this.usesCanvasSource) {
            this.texture.setSource(this.video);
            return;
        }

        if (!this.canvas || !this.canvasContext) {
            throw new Error('Canvas-backed alpha video texture source is unavailable');
        }

        const width = Math.max(1, this.video.videoWidth || this.canvas.width || 1);
        const height = Math.max(1, this.video.videoHeight || this.canvas.height || 1);
        if (this.canvas.width !== width || this.canvas.height !== height) {
            this.canvas.width = width;
            this.canvas.height = height;
        }

        this.canvasContext.clearRect(0, 0, width, height);
        this.canvasContext.drawImage(this.video, 0, 0, width, height);
        this.texture.setSource(this.canvas);
    }

    private normalizeTargetTime(targetTime: number) {
        const normalized = Number.isFinite(targetTime) ? Math.max(0, targetTime) : 0;
        const duration = this.video.duration;
        if (!Number.isFinite(duration) || duration <= 0) {
            return normalized;
        }

        if (this.video.loop) {
            const wrapped = normalized % duration;
            return wrapped >= 0 ? wrapped : wrapped + duration;
        }

        const maxTime = Math.max(0, duration - 1 / 240);
        return Math.min(normalized, maxTime);
    }

    private getSeekTolerance(frameRate: number) {
        return Math.max(1 / Math.max(frameRate, 1) / 4, 1 / 480);
    }

    private async waitForReadyFrame() {
        if (this.destroyed) return;
        if (this.video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
            return;
        }

        await new Promise<void>((resolve, reject) => {
            const onLoadedData = () => {
                cleanup();
                resolve();
            };

            const onError = () => {
                cleanup();
                reject(this.video.error ?? new Error('Failed to prepare video frame'));
            };

            const cleanup = () => {
                this.video.removeEventListener('loadeddata', onLoadedData);
                this.video.removeEventListener('error', onError);
            };

            this.video.addEventListener('loadeddata', onLoadedData, { once: true });
            this.video.addEventListener('error', onError, { once: true });
        });
    }

    private async seekToTime(targetTime: number, syncId: number, frameRate: number, precise = false) {
        if (this.destroyed) return;

        const tolerance = this.getSeekTolerance(frameRate);
        if (Math.abs(this.video.currentTime - targetTime) <= tolerance &&
            this.video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
            if (precise) {
                await this.waitForPresentedFrame(syncId);
            }
            return;
        }

        await new Promise<void>((resolve, reject) => {
            let settled = false;

            const cleanup = () => {
                if (timeout) {
                    window.clearTimeout(timeout);
                }
                this.video.removeEventListener('seeked', onSeeked);
                this.video.removeEventListener('error', onError);
            };

            const finishResolve = () => {
                if (settled) return;
                settled = true;
                cleanup();
                resolve();
            };

            const finishReject = (reason: unknown) => {
                if (settled) return;
                settled = true;
                cleanup();
                reject(reason);
            };

            const onSeeked = () => {
                if (this.destroyed || syncId !== this.syncSequence) {
                    finishResolve();
                    return;
                }
                finishResolve();
            };

            const onError = () => {
                finishReject(this.video.error ?? new Error('Failed seeking video texture frame'));
            };

            const timeout = window.setTimeout(() => {
                finishReject(new Error(`Timed out seeking video texture to ${targetTime.toFixed(4)}s`));
            }, 5000);

            this.video.addEventListener('seeked', onSeeked, { once: true });
            this.video.addEventListener('error', onError, { once: true });

            try {
                // fastSeek jumps to the nearest keyframe — much faster for
                // interactive scrubbing on WebM/VP9 videos.  Fall back to
                // precise currentTime assignment when fastSeek is unavailable
                // or when frame-accurate positioning is needed.
                if (!precise && typeof (this.video as any).fastSeek === 'function') {
                    (this.video as any).fastSeek(targetTime);
                } else {
                    this.video.currentTime = targetTime;
                }
            } catch (error) {
                finishReject(error);
            }
        });

        if (precise) {
            await this.waitForPresentedFrame(syncId);
        }
    }

    private async waitForPresentedFrame(syncId: number) {
        if (this.destroyed || syncId !== this.syncSequence) return;

        const anyVideo = this.video as HTMLVideoElement & {
            requestVideoFrameCallback?: (callback: (now: number, metadata: VideoFrameCallbackMetadata) => void) => number;
        };

        if (typeof anyVideo.requestVideoFrameCallback === 'function') {
            await new Promise<void>((resolve) => {
                let settled = false;

                const finish = () => {
                    if (settled) return;
                    settled = true;
                    resolve();
                };

                anyVideo.requestVideoFrameCallback?.(() => finish());
                window.setTimeout(finish, 120);
            });
            return;
        }

        await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
        await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
    }

    private resolveReady() {
        if (this.readySettled) return;
        this.readySettled = true;
        this.removeReadyListeners?.();
        this.readyResolve?.();
        this.readyResolve = null;
        this.readyReject = null;
    }

    private rejectReady(reason: unknown) {
        if (this.readySettled) return;
        this.readySettled = true;
        this.removeReadyListeners?.();
        this.readyReject?.(reason);
        this.readyResolve = null;
        this.readyReject = null;
    }
}

export { VideoTextureSource };
export type { VideoTexturePlaybackMode };
